/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/ChatPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { UserProfile } from '../types';
import ConfusionSolver from '../components/chatbot/ConfusionSolver';
//import SocraticTutor from '../components/chatbot/SocraticTutor';
import AdaptiveTutor from '../components/chatbot/AdaptiveTutor';
//import MathExplainer from '../components/chatbot/MathExplainer';
import IDDGuide from '../components/chatbot/IDDGuide';
//import ProMentor from '../components/chatbot/ProMentor';
//import BBPreneur from '../components/chatbot/BBPreneur';
import '../styles/components/ChatPage.css';
import MCQExamSimulator from '../components/chatbot/MCQExamSimulator';
import TheoryExamSimulator from '../components/chatbot/TheoryExamSimulator';
import DocumentQuestionExtractor from '../components/chatbot/DocumentQuestionExtractor';

const ChatPage: React.FC = () => {
  const { chatType } = useParams<{ chatType: string }>();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initialize user session (without forcing chat session yet)
  const initializeChatSession = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        setError('User not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        setError('Failed to load user profile.');
        setLoading(false);
        return;
      }

      setUserProfile(profile);
      
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Error initializing chat:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeChatSession();
  }, [chatType]);

  const createNewSession = async (): Promise<string> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: session, error: sessionError } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          title: `${chatType} Chat Session`,
          chat_type: chatType
        })
        .select()
        .single();

      if (sessionError) throw sessionError;
      
      setSessionId(session.id);
      return session.id;
      
    } catch (err: any) {
      console.error('Error creating chat session:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="chat-page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-page-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="chat-page-container">
        <div className="error-message">Please complete your profile first.</div>
      </div>
    );
  }

  const renderChatComponent = () => {
    const commonProps = {
      userProfile,
      onCreateSession: createNewSession
    };

    switch (chatType) {
      case 'confusion-solver':
        return <ConfusionSolver {...commonProps} />;
      /*case 'socratic-tutor':
        return <SocraticTutor {...commonProps} />; */
      case 'adaptive-tutor':
        return <AdaptiveTutor {...commonProps} />;
      /*case 'math-explainer':
        return <MathExplainer {...commonProps} />; */
      case 'idd-guide':
        return <IDDGuide {...commonProps} />;
      /*case 'pro-mentor':
        return <ProMentor {...commonProps} />;
      case 'bb-preneur':
        return <BBPreneur {...commonProps} />; */
      case 'mcq-exam-simulator':
        return <MCQExamSimulator {...commonProps} />;
      case 'theory-exam-simulator':
        return <TheoryExamSimulator {...commonProps} />;
      case 'document-question-extractor':
        return <DocumentQuestionExtractor {...commonProps} />;
        
      default:
        return <div className="error-message">Chat type "{chatType}" not found</div>;
    }
  };

  return (
    <div className="chat-page-container">
      {/* 🔹 Header removed — no more white bar */}
      {renderChatComponent()}
    </div>
  );
};

export default ChatPage;
